import { useEffect } from 'react'
import * as React from 'react'
import { Box, Typography, SxProps, Portal, Grid } from '@mui/material'
import {
  getFirstUpcomingOrActiveRound,
  getFirstUpcomingRound,
  isRoundJoined,
  isRoundStarted,
} from 'types/utils'
import { useSelectedRoundStore, useSelectedTabStore } from '@/store/store'
import { GameINFOV2, MenuTabs, PartnerPageData } from 'types/types'
import { useGetRound } from 'hooks/useGetRound'
import { useGetGameRounds } from 'hooks/useGetGameRounds'
import { useAuth } from 'context/JWTContext'
import { useLocalSelectionsState } from 'hooks/useLocalSelectionsState'
import { useGuardedNavigation } from 'hooks/useGuardedNavigation'
import Predictions from 'assets/icons/Predictions'
import StatsIcon from 'assets/icons/StatsIcon'
import useOnScreen from 'hooks/useOnScreen'
import { motion, AnimatePresence, MotionStyle } from 'framer-motion'
import Leaderboard from 'assets/icons/Leaderboard'
import { getDefaultTab, getWording, shouldHideUIForPOTM } from '@/lib/feature-flags/potm'
import { getStarSportsColor } from '@/utils/starSportsUtils'
import TrophyIcon from 'assets/icons/TrophyIcon'
import { useRouter } from 'next/router'

interface SectionSwitcherProps {
  pageTheme: PartnerPageData & { is_user_joined: boolean }
  game: GameINFOV2 | null
  disableSticky?: boolean
}

const SelectedTabStyles = (page: PartnerPageData): SxProps => {
  return {
    backgroundColor: `${getStarSportsColor(page)}30`,
    border: `2px solid ${getStarSportsColor(page)}`,
    height: '72px',
    width: '100%',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: `1px 2px 10px 0px ${getStarSportsColor(page)}30 inset`,
    borderRadius: '8px',
  }
}

const DefaultTabStyles: SxProps = {
  height: '72px',
  width: '100%',
  display: 'flex',
  gap: 1,
  cursor: 'pointer',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}

const DEFAULT_TABS_CONFIG = (game: GameINFOV2 | null, roundDeadlinePassed: boolean) => [
  {
    label: roundDeadlinePassed ? 'Results' : getWording(game).PREDICTIONS,
    icon: roundDeadlinePassed ? TrophyIcon : Predictions,
    value: 'predictions',
  },
  {
    label: 'Leaderboard',
    icon: Leaderboard,
    value: 'leaderboard',
  },
  {
    label: getWording(game).STATS,
    icon: StatsIcon,
    value: 'stats',
  },
]

const getTabConfig = (game: GameINFOV2 | null, roundDeadlinePassed: boolean) => {
  const isGameStarted = game?.start_time && new Date(game.start_time) < new Date()

  if (!shouldHideUIForPOTM(game)) {
    return DEFAULT_TABS_CONFIG(game, roundDeadlinePassed)
  }

  if (isGameStarted) {
    return [DEFAULT_TABS_CONFIG(game, roundDeadlinePassed)[2]] // stats
  }

  return [DEFAULT_TABS_CONFIG(game, roundDeadlinePassed)[0]] // voting
}

export default function SectionSwitcher({ pageTheme, game, disableSticky }: SectionSwitcherProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const isOnScreen = useOnScreen<React.RefObject<HTMLDivElement>>(ref)
  const [showSticky, setShowSticky] = React.useState(false)
  const [currentRound, setSelectedRound] = useSelectedRoundStore((state) => [
    state.selectedRound,
    state.setSelectedRound,
  ])
  const isGameStarted = game?.start_time && new Date(game.start_time) < new Date()
  const { isAuthenticated } = useAuth()
  const defaultTab = getDefaultTab(game, isAuthenticated)
  const router = useRouter()

  const [selectedTab, setSelectedTab] = useSelectedTabStore((state) => [
    state.selectedTab ?? defaultTab,
    state.setSelectedTab,
  ])

  const {
    localSelections,
    tiebreakers,
    clearRoundLocalSelections,
    clearRoundTiebreaker,
    bumpRoundRevertVersion,
    hasUnsavedChanges,
  } = useLocalSelectionsState()
  // pageTheme.is_user_joined is stale SSR-computed data once the user logs
  // out mid-session — require the live auth state too.
  const isUserJoined = Boolean(pageTheme?.is_user_joined) && isAuthenticated
  const isCurrentRoundJoined = game ? isRoundJoined(game, currentRound?.id, isUserJoined) : false
  const shouldGuardTabChange = isUserJoined && isCurrentRoundJoined && hasUnsavedChanges
  const guardNavigation = useGuardedNavigation(shouldGuardTabChange)
  const handleToggleChange = (value: MenuTabs | null) => {
    guardNavigation(() => setSelectedTab(value))
  }
  const isMultiRound = (game?.rounds?.length ?? 0) > 1
  const activeRoundId = game ? game.rounds?.[game.active_round_index]?.id : undefined
  useGetRound(activeRoundId, !isMultiRound && activeRoundId != null)
  useGetGameRounds(game?.id ?? 0, isMultiRound)

  const activeRound = game ? game.rounds[game.active_round_index] : null
  const roundDeadlinePassed = activeRound ? isRoundStarted(activeRound) : false

  useEffect(() => {
    if (!game || selectedTab !== 'predictions') return

    // RoundPicker's manual round selection (components/RoundPicker.tsx)
    // only updates the store, not the URL — so treat any already-selected
    // round that still belongs to this game as explicit too. Otherwise this
    // effect re-fires every time the tab flips back to Predictions and
    // silently snaps the user back to the active/upcoming round, discarding
    // whatever round they'd manually navigated to.
    const currentRoundBelongsToGame =
      !!currentRound && !!game.rounds?.some((r) => r.id === currentRound.id)
    if (currentRoundBelongsToGame) return

    const queryRoundId = router.isReady ? Number(router.query.roundId) || null : null
    const hasExplicitRound = !!(queryRoundId && game.rounds?.some((r) => r.id === queryRoundId))
    if (hasExplicitRound) return

    if (!isAuthenticated && isGameStarted) {
      setSelectedRound(getFirstUpcomingRound(game))
    } else {
      setSelectedRound(getFirstUpcomingOrActiveRound(game))
    }
  }, [
    game,
    selectedTab,
    isAuthenticated,
    isGameStarted,
    router.isReady,
    router.query.roundId,
    currentRound,
  ])

  useEffect(() => {
    if (selectedTab === 'predictions' || !game) return

    // Only drop draft picks for rounds that were never joined — an
    // already-joined round's edits are protected by the unsaved-changes
    // guard/bar and must survive a tab switch instead of being silently
    // discarded here.
    const roundIds = new Set([
      ...localSelections.map((s) => s.round_id),
      ...tiebreakers.map((t) => t.round_id),
    ])
    roundIds.forEach((roundId) => {
      if (!isRoundJoined(game, roundId, isUserJoined)) {
        clearRoundLocalSelections(roundId)
        clearRoundTiebreaker(roundId)
        bumpRoundRevertVersion(roundId)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  // Debounce sticky visibility to prevent flashes during layout shifts
  useEffect(() => {
    if (!disableSticky && !isOnScreen) {
      const timer = setTimeout(() => {
        setShowSticky(true)
      }, 150)
      return () => clearTimeout(timer)
    }
    setShowSticky(false)
  }, [disableSticky, isOnScreen])

  // TODO fix animation on mobile
  return (
    <>
      <AnimatePresence>
        {showSticky && (
          <Portal>
            <motion.div
              style={{
                position: 'fixed',
                top: 0,
                width: '100%',
                backgroundColor: 'white',
                height: '64px',
                padding: '0 12px',
                display: 'flex',
                alignItems: 'center',
                boxShadow: '1px 3px 16px 0px #0000001A',
                zIndex: 11,
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Grid
                container
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    width: '100%',
                    maxWidth: '1000px',
                    justifyContent: 'space-between',
                  }}
                >
                  {getTabConfig(game, roundDeadlinePassed).map((tab) => (
                    <Box
                      key={tab.value}
                      onClick={() => handleToggleChange(tab.value as MenuTabs)}
                      sx={{ width: '100%' }}
                    >
                      <Box
                        sx={
                          selectedTab === tab.value
                            ? { ...SelectedTabStyles(pageTheme), maxHeight: '48px' }
                            : { ...DefaultTabStyles, maxHeight: '48px' }
                        }
                      >
                        {tab.icon({
                          color:
                            selectedTab === tab.value
                              ? getStarSportsColor(pageTheme)
                              : 'rgba(137, 137, 137, 1)',
                          width: 24,
                          height: 24,
                          fill: 'none',
                        })}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
      <Box
        ref={ref}
        sx={{
          backgroundColor: 'white',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: '88px',
          gap: '8px',
          borderRadius: '16px',
          px: '12px',
          boxShadow: '1px 3px 16px 0px #0000001A',
        }}
      >
        {getTabConfig(game, roundDeadlinePassed).map((tab) => (
          <Box
            key={tab.value}
            onClick={() => handleToggleChange(tab.value as MenuTabs)}
            sx={{ width: '100%', position: 'relative' }}
          >
            {selectedTab === tab.value && game && (
              <motion.div
                layoutId="underline"
                style={{
                  ...(SelectedTabStyles(pageTheme) as MotionStyle),
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 50,
                }}
              />
            )}
            <Box sx={DefaultTabStyles}>
              {tab.icon({
                color:
                  selectedTab === tab.value
                    ? getStarSportsColor(pageTheme)
                    : 'rgba(137, 137, 137, 1)',
                width: 24,
                height: 24,
                fill: 'none',
              })}
              <Typography
                sx={{
                  fontSize: '12px',
                  color:
                    selectedTab === tab.value
                      ? getStarSportsColor(pageTheme)
                      : 'rgba(137, 137, 137, 1)',
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  )
}
